import {useEffect, useState} from "react";
import {Button, Checkbox, Col, DatePicker, Divider, Form, Input, message, Radio, Row, Select, Space} from "antd";
import {InputDropdownParameterDto} from "../../dto/InputDropdownParameterDto";
import {
    ACCOUNT_TYPE,
    COMMON_DROPDOWN,
    DATE_FORMAT,
    IDENTIFICATION_SOURCE,
    IDENTIFICATION_SOURCE_ID,
    INITIAL_PATTERN_VALIDATION,
    NIC_VALIDATION,
    ONLY_ALPHABETICAL_PATTERN_VALIDATION,
    PARAMETER_LIST,
} from "../../util/Constants";
import {DropdownListDto} from "../../dto/DropdownListDto";
import {CustomerWrapperDto} from "../../dto/CustomerWrapperDto";
import {CustomerDto} from "../../dto/CustomerDto";
import {PageStep} from "../../enums/PageStep";
import Validation from "../../util/Validation";
import './style.scss'
import dayjs from "dayjs";
import customerService from "../../service/customerService";
import {
    MSG_FAILED_TO_FETCH_COMMON_DROPDOWN_TYPES_ERROR,
    MSG_PLEASE_CONSENT_TO_THE_ABOVE_AGREEMENTS
} from "../../util/Messages";
import ErrorHandler from "../../util/ErrorHandler";
import {toLower, toNumber} from "lodash";
import {step_1_inactive_img} from '../../../AppImageConst';

interface UserDetailProps {
    getStepperFilledData: (data: CustomerWrapperDto) => void,
    currentCustomerDetail: CustomerWrapperDto,
    switchStep?: () => void,
    refNumber: string,
    accountTypeId: number,
    isFromSummaryView: boolean
}

const UserDetails = (props: UserDetailProps) => {
    const [identificationSourceTypeSelection, setIdentificationSourceTypeSelection] = useState(0);
    const [salutationList, setSalutationList] = useState<DropdownListDto[]>([]);
    const [nationalityList, setNationalityList] = useState<DropdownListDto[]>([]);
    const [genderList, setGenderList] = useState<DropdownListDto[]>([]);
    const [residencyList, setResidencyList] = useState<DropdownListDto[]>([]);
    const [identificationSourceTypeList, setIdentificationSourceTypeList] = useState<DropdownListDto[]>([]);
    const [agreementList, setAgreementList] = useState<DropdownListDto[]>([]);
    const [selectedAgreementList, setSelectedAgreementList] = useState<number[]>([]);

    const [form] = Form.useForm();

    const onChange = (e: Event) => {
        setIdentificationSourceTypeSelection(((e.target as HTMLInputElement).value) as unknown as number);
    };

    useEffect(() => {
        window.scrollTo(0, 0)
        getCommonDropdownList(props.currentCustomerDetail.identificationSource.id);

        //load identification source when submit on signup
        setIdentificationSourceTypeSelection(props.currentCustomerDetail.identificationSource.id)

        //if customer already filled the data , load the relevant data to form
        if (props.currentCustomerDetail.customerMstDto) {
            setFormValue(props.currentCustomerDetail)

        } else {
            //this load when customer object data not already have
            form.setFieldsValue(
                {
                    identificationSource: props.currentCustomerDetail.identificationSource.id,
                    identificationValue: props.currentCustomerDetail.identificationValue,
                })
        }

    }, [])

    const setFormValue = (customerWrapperDto: CustomerWrapperDto) => {
        form.setFieldsValue(
            {
                identificationSource: customerWrapperDto.identificationSource.id,
                identificationValue: customerWrapperDto.identificationValue,
                ppOrDlExpiryDate: dayjs(customerWrapperDto.customerMstDto?.ppOrDlExpiryDate),
                mothersMaidenName: customerWrapperDto.customerMstDto?.mothersMaidenName,
                residencyStatus: toNumber(customerWrapperDto.customerMstDto?.residencyStatus),
                nationality: toNumber(customerWrapperDto.customerMstDto?.nationality),
                dateOfBirth: dayjs(customerWrapperDto.customerMstDto.dateOfBirth),
                gender: toNumber(customerWrapperDto.customerMstDto?.gender),
                nameToAppearOnStatement: customerWrapperDto.customerMstDto?.nameToAppearOnStatement,
                surname: customerWrapperDto.customerMstDto?.surname,
                namesDenotedByInitials: customerWrapperDto.customerMstDto?.namesDenotedByInitials,
                initials: customerWrapperDto.customerMstDto?.initials,
                title: toNumber(customerWrapperDto.customerMstDto?.title),
                nicNo: customerWrapperDto.customerMstDto.nicNo,
            }
        )

        // default check the page wise agreement lists since customer already filled data
        form.setFieldsValue({agreementList: customerWrapperDto.customerMstDto.pageWiseAgreementIdList});


    }

    //detect the form changes
    const validateFieldChanges = (customerDto: CustomerDto, fieldValues: CustomerDto) => {

        if (customerDto.mothersMaidenName != fieldValues.mothersMaidenName) {
            return true;
        } else if (customerDto.residencyStatus != fieldValues.residencyStatus) {
            return true;
        } else if (customerDto.gender != fieldValues.gender) {
            return true;
        } else if (customerDto.nationality != fieldValues.nationality) {
            return true;
        } else if (customerDto.dateOfBirth != dayjs(fieldValues.dateOfBirth).format(DATE_FORMAT)) {
            return true;
        } else if (customerDto.nameToAppearOnStatement != fieldValues.nameToAppearOnStatement) {
            return true;
        } else if (customerDto.surname != fieldValues.surname) {
            return true;
        } else if (customerDto.initials != fieldValues.initials) {
            return true;
        } else if (customerDto.title != fieldValues.title) {
            return true;
        } else if (customerDto.identificationValue != fieldValues.identificationValue) {
            return true;

        } else if (customerDto.identificationSource?.sourceType !== IDENTIFICATION_SOURCE.NIC && customerDto.nicNo != fieldValues.nicNo) {
            return true;
        } else if (customerDto.namesDenotedByInitials != fieldValues.namesDenotedByInitials) {
            return true;
        } else if (identificationSourceTypeSelection !== IDENTIFICATION_SOURCE_ID.NIC) {
            if (customerDto.ppOrDlExpiryDate != dayjs(fieldValues.ppOrDlExpiryDate).format(DATE_FORMAT))
                return true;
        }
    }

    /**
     * START of drop down parameter data load on component load
     **/
    const getCommonDropdownList = (identificationSourceTypeSelection: number) => {
        const inputDropdownParameterDtoList: InputDropdownParameterDto[] = [
            {
                dropdownDataSourceId: COMMON_DROPDOWN.SALUTATION,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.GENDER,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.NATIONALITY,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.RESIDENT,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.IDENTIFICATION_SOURCE,
                findTextValue: "",
                parameterList: [PARAMETER_LIST.ACTIVE]
            },
            {
                dropdownDataSourceId: COMMON_DROPDOWN.AGREEMENT,
                findTextValue: "",
                parameterList: [[identificationSourceTypeSelection], [PageStep.STEP_PERSONNEL_DETAIL], [PARAMETER_LIST.ACTIVE]]
            },
        ]

        loadCommonDropdownDataList(inputDropdownParameterDtoList);
    }

    const loadCommonDropdownDataList = (dropDownParameterDto: InputDropdownParameterDto[]) => {
        customerService.getCommonDropdownList(dropDownParameterDto).then(res => {
            if (res.data !== null) {
                let dropdownDataList = res.data.results.dropdownDataList;
                setSalutationList(dropdownDataList[COMMON_DROPDOWN.SALUTATION])
                setNationalityList(dropdownDataList[COMMON_DROPDOWN.NATIONALITY])
                setGenderList(dropdownDataList[COMMON_DROPDOWN.GENDER])
                setResidencyList(dropdownDataList[COMMON_DROPDOWN.RESIDENT])
                setIdentificationSourceTypeList(dropdownDataList[COMMON_DROPDOWN.IDENTIFICATION_SOURCE])
                setAgreementList(dropdownDataList[COMMON_DROPDOWN.AGREEMENT])

            } else {
                message.error(MSG_FAILED_TO_FETCH_COMMON_DROPDOWN_TYPES_ERROR).then();
            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
        })
    }

    /**
     * This functions sends the form data to parent component. before pass date it checks whether the customer
     * data already exists by checking 'props.currentCustomerDetail.customerMstDto'. if so perform another validation
     * to capture the changes on the form against the props.currentCustomerDetail.customerMstDto. if capture a change
     * it performs an update by adding the 'recordStatus = UPDATE' otherwise just switch the step no next.
     *
     * if no props.currentCustomerDetail.customerMstDto performs new customer save by passing the date to parent component
     * by adding the 'recordStatus = SAVE'
     **/
    const submitFirstStep = (values: CustomerDto) => {
        let identificationSourceDto = {
            identificationValue: props.currentCustomerDetail.identificationValue,
            sourceType: identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.NIC ? IDENTIFICATION_SOURCE.NIC :
                identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.DD ? IDENTIFICATION_SOURCE.DL : IDENTIFICATION_SOURCE.PP,
            id: identificationSourceTypeSelection,
            recordStatus: 'ACTIVE'
        }


        //checks the customer data already exists
        if (props.currentCustomerDetail.customerMstDto != null) {
            if (validateFieldChanges(props.currentCustomerDetail.customerMstDto, values)) {
                //if update should pass the current customer id to the backend
                values.id = props.currentCustomerDetail.customerMstDto.id

                const customerObj: CustomerDto = props.currentCustomerDetail.customerMstDto

                if (identificationSourceTypeSelection !== IDENTIFICATION_SOURCE_ID.NIC) {
                    customerObj.ppOrDlExpiryDate = dayjs(values.ppOrDlExpiryDate).format(DATE_FORMAT)
                }
                if (identificationSourceTypeSelection == IDENTIFICATION_SOURCE_ID.NIC) {
                    customerObj.nicNo = values.identificationValue
                } else {
                    customerObj.nicNo = values.nicNo
                }
                customerObj.identificationValue = values.identificationValue
                customerObj.identificationSource = props.currentCustomerDetail.customerMstDto.identificationSource
                customerObj.mothersMaidenName = values.mothersMaidenName
                customerObj.residencyStatus = values.residencyStatus
                customerObj.nationality = values.nationality
                customerObj.dateOfBirth = dayjs(values.dateOfBirth).format(DATE_FORMAT)
                customerObj.gender = values.gender
                customerObj.nameToAppearOnStatement = values.nameToAppearOnStatement
                customerObj.surname = values.surname
                customerObj.namesDenotedByInitials = values.namesDenotedByInitials
                customerObj.initials = values.initials
                customerObj.title = values.title


                //Update the customer detail
                let customerDto = {
                    customerMstDto: customerObj,
                    completedStep: props.currentCustomerDetail.completedStep,
                    currentActiveStep: props.currentCustomerDetail.completedStep,
                    recordStatus: "UPDATE",
                    isFinalStep: false,
                    identificationSource: identificationSourceDto,
                    identificationValue: props.currentCustomerDetail.identificationValue
                } as CustomerWrapperDto
                props.getStepperFilledData(customerDto)
            } else {
                //just switch next step since no change detected

                if (props.switchStep) props.switchStep()

            }
        } else {
            //new customer detail save

            //retrieve and fill customerdto email and mobileNo from wrapperDto
            values.email = props.currentCustomerDetail.email
            values.mobileNo = props.currentCustomerDetail.mobileNumber
            values.pageWiseAgreementIdList = selectedAgreementList
            values.initialAccountTypeId = props.accountTypeId
            values.dateOfBirth = dayjs(values.dateOfBirth).format(DATE_FORMAT)
            if (props.accountTypeId === ACCOUNT_TYPE.JOINT_ACCOUNT) {
                values.jointReferenceNumber = props.refNumber !== "" ? props.refNumber : null
            }
            values.identificationSource = props.currentCustomerDetail.identificationSource
            if (identificationSourceTypeSelection == IDENTIFICATION_SOURCE_ID.NIC) {
                values.nicNo = values.identificationValue
            }else {
                values.ppOrDlExpiryDate = dayjs(values.ppOrDlExpiryDate).format(DATE_FORMAT)
            }

            let customerDto = {
                customerMstDto: values,
                completedStep: PageStep.STEP_PERSONNEL_DETAIL,
                currentActiveStep: PageStep.STEP_PERSONNEL_DETAIL,
                recordStatus: "SAVE",
                isFinalStep: false,
                identificationSource: identificationSourceDto,
                identificationValue: props.currentCustomerDetail.identificationValue,

            } as CustomerWrapperDto
            props.getStepperFilledData(customerDto)
        }


    }

    //capture and add selected pagewise agreement ids to state
    const onChangeAgreement = (value: number[]) => {
        setSelectedAgreementList(value);
    }
    const validateAgreements = (rule: any, value: any, callback: any) => {
        if (undefined == value || !agreementList.every((a: DropdownListDto) => value.includes(a.value as number))) {
            callback(MSG_PLEASE_CONSENT_TO_THE_ABOVE_AGREEMENTS)
        } else {
            callback()
        }
    };

    const getPlaceHolderByIdentificationType = (identificationType: string | undefined) => {
        if (undefined != identificationType) {
            return IDENTIFICATION_SOURCE.NIC === identificationType ? identificationType : toLower(identificationType)
        }
        return "N/A"
    }
    const inputOnBlur = (evt: any) => {

        let value = evt.target.value.trim().replace(/\s+/g, ' ')
            .split(' ')
            .map((word: string) => word.split('').join(' '))
            .join(' ');
        form.setFieldsValue({initials: value})
        form.validateFields(['initials'])
    }


    const inputOnFocus = (evt: any) => {
        if (evt.target.value.length > 0 && !evt.target.value.endsWith(" ")) {
            form.setFieldsValue({initials: evt.target.value.concat(" ")})
            form.validateFields(['initials'])
        }
    }
    const onKeyDown = (event: KeyboardEvent): void => {
        if (event.code === 'Space') event.preventDefault()
    }
    return (

        <div>
            <p className='step_card_header'>
                <div className="icon-container">
                    <img className='header-icon-img' src={step_1_inactive_img}/>
                </div>
                Tell Us All About Yourself
            </p>

            <Form
                className='form-input'
                layout={"vertical"}
                form={form}
                onFinish={submitFirstStep}
                disabled={props.isFromSummaryView}

            >

                <Row gutter={[32, 0]}>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Title"
                            name={"title"}
                            rules={!props.isFromSummaryView ? [{required: true, message: 'Please select title'}] : []}>
                            <Select placement='bottomLeft' placeholder={"Select title"} listHeight={180}
                                    getPopupContainer={(trigger: { parentNode: any; }) => trigger.parentNode}
                                    style={{width: '100%'}}
                                    options={salutationList}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Initials"
                            onKeyPress={Validation.onlyAlphabeticWithSpaceAndDot}
                            name={"initials"}
                            onKeyDown={onKeyDown}
                            rules={!props.isFromSummaryView ? [
                                {required: true, message: 'Please enter initials'},
                                {whitespace: true, message: 'Cannot contain whitespaces'},
                                {max: 20, message: 'The maximum length is 10 characters'},
                                {
                                    pattern: INITIAL_PATTERN_VALIDATION,
                                    message: 'Special characters and numbers are not allowed'
                                }
                            ] : []}>
                            <Input placeholder={"Enter initials"}
                                   onInput={(e: any) => {
                                       if (e.nativeEvent.inputType !== "deleteContentBackward") {
                                           e.target.value = ("" + e.target.value).toUpperCase().concat(" ")
                                       }
                                   }} onBlur={inputOnBlur} onClick={inputOnFocus}/>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Names Denoted by Initials"
                            onKeyPress={Validation.onlyAlphabeticWithSpace}
                            name={"namesDenotedByInitials"}
                            rules={!props.isFromSummaryView ? [
                                {required: true, message: 'Please enter names denoted by initials'},
                                {whitespace: true, message: 'Cannot contain whitespaces'},
                                {max: 50, message: 'The maximum length is 50 characters'},
                                {
                                    pattern: ONLY_ALPHABETICAL_PATTERN_VALIDATION,
                                    message: 'Special characters and numbers are not allowed'
                                }
                            ] : []}>
                            <Input placeholder={"Enter names denoted by initials"}/>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Surname"
                            onKeyPress={Validation.onlyAlphabeticWithSpace}
                            name={"surname"}
                            rules={!props.isFromSummaryView ? [
                                {required: true, message: 'Please enter surname'},
                                {whitespace: true, message: 'Cannot contain whitespaces'},
                                {max: 50, message: 'The maximum length is 50 characters'},
                                {
                                    pattern: ONLY_ALPHABETICAL_PATTERN_VALIDATION,
                                    message: 'Special characters and numbers are not allowed'
                                }
                            ] : []}>
                            <Input placeholder={"Enter surname"}/>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Name to Appear on Your Statement"
                            onKeyPress={Validation.onlyAlphabeticWithSpaceAndDot}
                            name={"nameToAppearOnStatement"}
                            rules={!props.isFromSummaryView ? [
                                {required: true, message: 'Please enter name to appear on your statement'},
                                {whitespace: true, message: 'Cannot contain whitespaces'},
                                {max: 30, message: 'The maximum length is 30 characters'},
                                // {
                                //     pattern: ONLY_ALPHABETICAL_PATTERN_VALIDATION,
                                //     message: 'Special characters and numbers are not allowed'
                                // }
                            ] : []}>
                            <Input placeholder="Enter name to appear on your statement"/>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Gender"
                            name={"gender"}
                            rules={!props.isFromSummaryView ? [{required: true, message: 'Please select gender'}] : []}>
                            <Select placement='bottomLeft' listHeight={180}
                                placeholder={"Select gender"}
                                getPopupContainer={(trigger: { parentNode: any; }) => trigger.parentNode}
                                style={{width: '100%'}}
                                options={genderList}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Date of Birth"
                            name={"dateOfBirth"}
                            onKeyPress={Validation.calenderDateValidation}
                            rules={!props.isFromSummaryView ? [{
                                required: true,
                                message: 'Please select date of birth'
                            }] : []}>
                            <DatePicker className={'datepicker-small-size'} placement='bottomLeft' showToday={false} disabledDate={(d: {
                                isAfter: (arg0: dayjs.Dayjs) => any;
                            }) => !d || d.isAfter(dayjs().add(-1, 'day'))} style={{width: '100%'}}
                                        placeholder={"Select date of birth"}/>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Nationality"
                            name={"nationality"}
                            rules={!props.isFromSummaryView ? [{
                                required: true,
                                message: 'Please select nationality'
                            }] : []}>
                            <Select placement='bottomLeft' listHeight={180}
                                placeholder={"Select nationality"}
                                getPopupContainer={(trigger: { parentNode: any; }) => trigger.parentNode}
                                style={{width: '100%'}}
                                options={nationalityList}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label="Residency Status"
                            name={"residencyStatus"}
                            rules={!props.isFromSummaryView ? [{
                                required: true,
                                message: 'Please select residency status'
                            }] : []}>
                            <Select placement='bottomLeft' listHeight={180}
                                placeholder={"Select residency status"}
                                getPopupContainer={(trigger: { parentNode: any; }) => trigger.parentNode}
                                style={{width: '100%'}}
                                options={residencyList}
                            />
                        </Form.Item>
                    </Col>

                    {
                        props.currentCustomerDetail.identificationSource.sourceType !== IDENTIFICATION_SOURCE.NIC ?
                            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                <Form.Item
                                    name={"nicNo"}
                                    label={"NIC"}
                                    onKeyPress={Validation.nicFieldValidation}
                                    rules={!props.isFromSummaryView ? [
                                        {
                                            required: true,
                                            message: 'Please enter NIC number'
                                        },
                                        {
                                            pattern: NIC_VALIDATION,
                                            message: "Please enter a valid NIC number"
                                        },
                                    ] : []}>
                                    <Input placeholder={"Enter NIC number"}/>
                                </Form.Item>
                            </Col> : <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                <Form.Item
                                    label="Mother`s Maiden Name"
                                    onKeyPress={Validation.onlyAlphabeticWithSpace}
                                    name={"mothersMaidenName"}
                                    rules={!props.isFromSummaryView ? [
                                        {required: true, message: 'Please enter mother`s Maiden Name'},
                                        {whitespace: true, message: 'Cannot contain whitespaces'},
                                        {max: 20, message: 'The maximum length is 20 characters'},
                                        {
                                            pattern: ONLY_ALPHABETICAL_PATTERN_VALIDATION,
                                            message: 'Special characters and numbers are not allowed'
                                        }
                                    ] : []}>
                                    <Input placeholder={"Enter mother`s maiden name"}/>
                                </Form.Item>
                            </Col>
                    }

                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                            label=""
                            name={"identificationSource"}
                            rules={!props.isFromSummaryView ? [{
                                required: true,
                                message: 'Please select identification source'
                            }] : []}>
                            <Radio.Group className='radio_group_set radio_group_set_step_1' onChange={onChange}>
                                <Row>
                                    {identificationSourceTypeList.map((data: DropdownListDto) => {
                                        return (<>
                                            <Col>
                                                <Radio disabled value={data.value}>{data.label}</Radio>
                                            </Col>
                                        </>)
                                    })}
                                </Row>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={0} xs={0}>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        {identificationSourceTypeSelection > 0 ?
                            <Form.Item
                                name={"identificationValue"}
                                label={props.currentCustomerDetail.identificationSource.sourceType.concat(" Number")}
                                onKeyPress={(props.currentCustomerDetail.identificationSource.sourceType === IDENTIFICATION_SOURCE.NIC) ? Validation.nicFieldValidation : Validation.isNumbersAndLetters}
                                rules={!props.isFromSummaryView ? [
                                    {
                                        required: true,
                                        message: 'Please enter '.concat(getPlaceHolderByIdentificationType(props.currentCustomerDetail.identificationSource.sourceType)).concat(" number")
                                    },
                                    (props.currentCustomerDetail.identificationSource.sourceType !== IDENTIFICATION_SOURCE.NIC) ? {
                                        max: 15,
                                        message: 'The maximum length is 15 characters'
                                    } : {},
                                    (props.currentCustomerDetail.identificationSource.sourceType === IDENTIFICATION_SOURCE.NIC) ? {
                                        pattern: NIC_VALIDATION,
                                        message: "Please enter a valid NIC number"
                                    } : {},
                                ] : []}>
                                <Input placeholder={identificationSourceTypeSelection === 1 ? "Enter NIC number" :
                                    identificationSourceTypeSelection === 3 ? "Enter driving license number" : "Enter passport number"}/>
                            </Form.Item> : ''
                        }
                    </Col>

                    {(identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.PP ||
                        identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.DD) ?
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                            <Form.Item
                                label={identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.PP ?
                                    "Passport Expiry Date" : "Driving License Expiry Date"}
                                name={"ppOrDlExpiryDate"}
                                onKeyPress={Validation.calenderDateValidation}
                                rules={!props.isFromSummaryView ? [{
                                    required: true,
                                    message: identificationSourceTypeSelection === IDENTIFICATION_SOURCE_ID.PP ?
                                        "Please select passport expiry date" : "Please select driving license expiry date"
                                }] : []}>
                                <DatePicker className={'datepicker-small-size'} placement='bottomLeft' showToday={false} disabledDate={(d: { isBefore: (arg0: dayjs.Dayjs) => any }) =>
                                    !d || d.isBefore(dayjs())}
                                            placeholder={'Select expiry date'} style={{width: '100%'}}/>
                            </Form.Item>
                        </Col> : <></>
                    }
                    {
                        props.currentCustomerDetail.identificationSource.sourceType !== IDENTIFICATION_SOURCE.NIC ?
                            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                <Form.Item
                                    label="Mother`s Maiden Name"
                                    onKeyPress={Validation.onlyAlphabeticWithSpace}
                                    name={"mothersMaidenName"}
                                    rules={!props.isFromSummaryView ? [
                                        {required: true, message: 'Please enter mother`s Maiden Name'},
                                        {whitespace: true, message: 'Cannot contain whitespaces'},
                                        {max: 20, message: 'The maximum length is 20 characters'},
                                        {
                                            pattern: ONLY_ALPHABETICAL_PATTERN_VALIDATION,
                                            message: 'Special characters and numbers are not allowed'
                                        }
                                    ] : []}>
                                    <Input placeholder={"Enter mother`s maiden name"}/>
                                </Form.Item>
                            </Col> : ''
                    }

                    <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                        <Form.Item
                            name={"agreementList"}
                            label=""
                            className="step_1_agreementList"
                            valuePropName="checked"
                            rules={[{validator: validateAgreements}]}
                        >
                            <Checkbox.Group
                                disabled={props.currentCustomerDetail.customerMstDto != null}
                                defaultValue={props.currentCustomerDetail.customerMstDto?.pageWiseAgreementIdList}
                                onChange={onChangeAgreement}>
                                {agreementList && (
                                    agreementList.map((data) => {
                                        return (
                                            <Checkbox className="step_1_checkbox"
                                                      value={data.value}>{data.label}</Checkbox>
                                        )
                                    })
                                )
                                }
                            </Checkbox.Group>
                        </Form.Item>
                    </Col>
                </Row>
                {!props.isFromSummaryView ?
                    <>
                        <Row>
                            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                                <Divider/>
                            </Col>
                        </Row>
                        <Space style={{float: 'right'}} className='form_next_btn_col'>
                            <Form.Item>
                                <Button className='form_next_btn' htmlType="submit" type="primary">
                                    Next
                                </Button>
                            </Form.Item>
                        </Space>
                    </> : ''
                }

            </Form>


        </div>
    )

}
export default UserDetails;

